import React from 'react'

export interface BadgeChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  label?: React.ReactNode
  icon?: string | null
  active?: boolean
  variant?: 'default' | 'pulse' | 'electric'
  children?: React.ReactNode
}

const CSS = `
.wst-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: var(--radius-full, 9999px);
  user-select: none;
  transition: all 180ms ease;
}
.wst-chip:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.16);
}

.wst-chip--electric {
  background: rgba(56, 191, 255, 0.08);
  border-color: rgba(56, 191, 255, 0.25);
}
.wst-chip--electric .wst-chip__label {
  color: var(--c-accent-electric, #38BFFF);
}

.wst-chip--pulse {
  background: rgba(42, 230, 182, 0.08);
  border-color: rgba(42, 230, 182, 0.25);
}
.wst-chip--pulse .wst-chip__label {
  color: var(--c-pulse, #2AE6B6);
}

.wst-chip__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: currentColor;
}
.wst-chip__label {
  color: rgba(255, 255, 255, 0.85);
  font-family: var(--font-data, monospace);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
`

if (typeof document !== 'undefined' && !document.getElementById('wst-chip-css')) {
  const s = document.createElement('style')
  s.id = 'wst-chip-css'
  s.textContent = CSS
  document.head.appendChild(s)
}

export const BadgeChip: React.FC<BadgeChipProps> = ({
  label,
  icon = null,
  variant = 'default',
  children,
  className = '',
  ...rest
}) => {
  const cls = ['wst-chip', `wst-chip--${variant}`, className].filter(Boolean).join(' ')

  return (
    <span className={cls} {...rest}>
      {icon && (
        <span className="wst-chip__icon" aria-hidden="true">
          <i className={`pi ${icon}`} />
        </span>
      )}
      <span className="wst-chip__label">{label ?? children}</span>
    </span>
  )
}
